import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import type { NoticeWithDetails } from "../../gedoba/notices/queries";
import React, { useState, useMemo } from "react";
import { useQuery } from "wasp/client/operations";
import { getLocationNotices } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Bell,
  AlertTriangle,
  Info,
  Calendar,
  User,
  MapPin,
  ExternalLink,
  Edit3,
  Trash2,
  Eye,
  Pin,
  Archive,
  Grid3X3,
  List,
  Table,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedPageHeader,
  EnhancedFilterSection,
  ResponsiveViewSwitcher,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";

type NoticeCategory =
  | "announcement"
  | "warning"
  | "info"
  | "maintenance"
  | "event"
  | "general"
  | "all";
type NoticePriority = "low" | "medium" | "high" | "all";

const NOTICE_CATEGORIES = [
  { value: "all", label: "All Categories", icon: Filter },
  { value: "announcement", label: "Announcement", icon: Bell },
  { value: "warning", label: "Warning", icon: AlertTriangle },
  { value: "info", label: "Information", icon: Info },
  { value: "maintenance", label: "Maintenance", icon: null },
  { value: "event", label: "Event", icon: Calendar },
  { value: "general", label: "General", icon: null },
];

const NOTICE_PRIORITIES = [
  { value: "all", label: "All Priorities" },
  { value: "high", label: "High Priority" },
  { value: "medium", label: "Medium Priority" },
  { value: "low", label: "Low Priority" },
];

export default function NoticeBoardPage() {
  useRequireSectionVisible("showNotice");
  const { data: user } = useAuth();
  const { selectedLocation, childLocationFilter } = useLocationContext();
  const navigate = useNavigate();

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<NoticeCategory>("all");
  const [selectedPriority, setSelectedPriority] =
    useState<NoticePriority>("all");
  const [sortBy, setSortBy] = useState<
    "newest" | "oldest" | "priority" | "title"
  >("newest");
  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch notices using hub aggregation-aware query
  const {
    data: notices = [],
    isLoading,
    error,
  } = useQuery(
    getLocationNotices,
    {
      locationSlug: selectedLocation?.locationCode || "",
      filters: {
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        priority: selectedPriority !== "all" ? selectedPriority : undefined,
        searchTerm: searchTerm || undefined,
      },
      childLocationFilter: childLocationFilter || undefined,
    },
    {
      enabled: !!selectedLocation?.locationCode,
    },
  ) as { data: NoticeWithDetails[]; isLoading: boolean; error: any };

  // Filter and sort notices
  const filteredAndSortedNotices = useMemo(() => {
    let filtered: NoticeWithDetails[] = notices;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (notice) =>
          notice.title.toLowerCase().includes(searchLower) ||
          notice.description?.toLowerCase().includes(searchLower) ||
          notice.contentBody?.toLowerCase().includes(searchLower) ||
          notice.category?.toLowerCase().includes(searchLower) ||
          notice.tags.some((tag) => tag.toLowerCase().includes(searchLower)),
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "oldest":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "priority":
          const priorityOrder: { [key: string]: number } = {
            high: 3,
            medium: 2,
            low: 1,
          };
          const aPriority =
            priorityOrder[a.metadata?.priority as string] ||
            priorityOrder["low"];
          const bPriority =
            priorityOrder[b.metadata?.priority as string] ||
            priorityOrder["low"];
          return bPriority - aPriority;
        case "title":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [notices, searchTerm, selectedCategory, selectedPriority, sortBy]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getCategoryIcon = (category: string) => {
    const categoryData = NOTICE_CATEGORIES.find(
      (cat) => cat.value === category,
    );
    if (categoryData?.icon) {
      const IconComponent = categoryData.icon;
      return <IconComponent className="w-4 h-4" />;
    }
    return <Bell className="w-4 h-4" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Today";
    if (diffDays === 2) return "Yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view notices.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              No Location Selected
            </h2>
            <p className="text-gray-600">
              Please select a location to view notices.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Removed header collapse functionality; header always visible

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Notice Board
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Explore notices and announcements at {selectedLocation?.name || "Location"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  variant={isFilterExpanded ? "primary" : "secondary"}
                  className="hidden lg:flex"
                />

                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search notices..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) =>
                            setSelectedCategory(e.target.value as NoticeCategory)
                          }
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {NOTICE_CATEGORIES.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Priority</label>
                        <select
                          value={selectedPriority}
                          onChange={(e) =>
                            setSelectedPriority(e.target.value as NoticePriority)
                          }
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {NOTICE_PRIORITIES.map((priority) => (
                            <option key={priority.value} value={priority.value}>
                              {priority.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="priority">Priority</option>
                          <option value="title">Title A-Z</option>
                        </select>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user?.isAdmin && (
                  <EnhancedActionButton
                  onClick={() => {
                    if (selectedLocation?.locationCode) {
                      navigate(
                        `/locations/${selectedLocation.locationCode}/notices/add`,
                      );
                    } else {
                      navigate("/notices/add");
                    }
                  }}
                  icon={Plus}
                  label={
                    <span className="hidden sm:inline text-white">
                      Post Notice
                    </span>
                  }
                  />
                )}
              </div>
            </div>
          </div>
        }
        filters={
          <div 
            className={cn(
              "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
              isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
            )}
          >
          <div className="px-8 py-6 space-y-3">
            {/* Column Headers - Only for table view */}
            {viewMode === "table" && (
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                <div className="col-span-3 min-w-0 truncate">NOTICE</div>
                <div className="col-span-2 min-w-0 truncate">CATEGORY</div>
                <div className="col-span-1 min-w-0 truncate">PRIORITY</div>
                <div className="col-span-2 min-w-0 truncate">AUTHOR</div>
                <div className={user?.isAdmin ? "col-span-2 min-w-0 truncate" : "col-span-3 min-w-0 truncate"}>DATE</div>
                {user?.isAdmin ? <div className="col-span-1 min-w-0 truncate">VIEWS</div> : <div className="col-span-1 text-right min-w-0 truncate">VIEWS</div>}
                {user?.isAdmin && <div className="col-span-1 text-right min-w-0 truncate">ACTIONS</div>}
              </div>
            )}
            {/* Column Filters */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Notice Filter */}
                <div className="col-span-4">
                  <input
                    type="text"
                    placeholder="Search notices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {/* Category Filter */}
                <div className="col-span-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) =>
                      setSelectedCategory(e.target.value as NoticeCategory)
                    }
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {NOTICE_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Priority Filter */}
                <div className="col-span-1">
                  <select
                    value={selectedPriority}
                    onChange={(e) =>
                      setSelectedPriority(e.target.value as NoticePriority)
                    }
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {NOTICE_PRIORITIES.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Author Filter - placeholder */}
                <div className="col-span-2">
                  <select
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled
                  >
                    <option value="All">All</option>
                  </select>
                </div>
                {/* Date Filter - Sort selector */}
                <div className="col-span-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="priority">Priority</option>
                    <option value="title">Title A-Z</option>
                  </select>
                </div>
                {/* Views Filter - placeholder */}
                <div className="col-span-1">
                  <div className="w-full px-3 py-2"></div>
                </div>
              </div>
            </div>
          </div>

        }
      >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading notices...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Notices
                  </h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredAndSortedNotices.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Notices Found
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm ||
                    selectedCategory !== "all" ||
                    selectedPriority !== "all"
                      ? "No notices match your current filters."
                      : "Be the first to post a notice for this location."}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && filteredAndSortedNotices.length > 0 && (
              <>
                {/* Cards View - Use enhanced layout with padding */}
                {viewMode === "cards" && (
                  <div className="px-8 py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredAndSortedNotices.map((notice) => (
                        <div
                          key={notice.id}
                          className={`${
                            enhancedPageLayoutStyles.card.container
                          } ${
                            notice.isPinned ? "border-blue-500 shadow-sm" : ""
                          } cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => navigate(`/notices/${notice.id}`)}
                        >
                          <div
                            className={enhancedPageLayoutStyles.card.content}
                          >
                            <div
                              className={enhancedPageLayoutStyles.card.header}
                            >
                              <div className="flex items-center gap-2">
                                {getCategoryIcon(notice.category || "general")}
                                <span className="text-sm font-medium text-gray-600 capitalize">
                                  {notice.category || "General"}
                                </span>
                                {notice.isPinned && (
                                  <Pin className="w-4 h-4 text-blue-600" />
                                )}
                              </div>

                              {notice.metadata?.priority && (
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                                    notice.metadata.priority,
                                  )}`}
                                >
                                  {notice.metadata.priority
                                    .charAt(0)
                                    .toUpperCase() +
                                    notice.metadata.priority.slice(1)}
                                </span>
                              )}
                            </div>

                            <h3 className={enhancedPageLayoutStyles.card.title}>
                              {notice.title}
                            </h3>

                            {notice.description && (
                              <p
                                className={
                                  enhancedPageLayoutStyles.card.description
                                }
                              >
                                {notice.description}
                              </p>
                            )}

                            {/* Notice Content Preview */}
                            {notice.contentBody && (
                              <div className="px-4 py-3 border-b border-border">
                                <p className="text-gray-700 text-sm line-clamp-4">
                                  {notice.contentBody}
                                </p>
                              </div>
                            )}

                            {/* Notice Images */}
                            {notice.images && notice.images.length > 0 && (
                              <div className="px-4 py-3 border-b border-border">
                                <div className="grid grid-cols-2 gap-2">
                                  {notice.images
                                    .slice(0, 4)
                                    .map((image, index) => (
                                      <img
                                        key={index}
                                        src={image}
                                        alt={`Notice image ${index + 1}`}
                                        className="w-full h-20 object-cover rounded-lg"
                                      />
                                    ))}
                                </div>
                                {notice.images.length > 4 && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    +{notice.images.length - 4} more images
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Tags */}
                            {notice.tags && notice.tags.length > 0 && (
                              <div className="px-4 py-2 border-b border-border">
                                <div className="flex flex-wrap gap-1">
                                  {notice.tags.slice(0, 3).map((tag, index) => (
                                    <span
                                      key={index}
                                      className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                  {notice.tags.length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{notice.tags.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            <div
                              className={enhancedPageLayoutStyles.card.footer}
                            >
                              <div
                                className={enhancedPageLayoutStyles.card.meta}
                              >
                                <div className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  <span>
                                    {notice.author?.email || "Anonymous"}
                                  </span>
                                </div>
                                <span>{formatDate(notice.createdAt)}</span>
                                <div className="flex items-center gap-1">
                                  <Eye className="w-4 h-4" />
                                  <span>{notice.viewCount || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* List View - Use enhanced layout with padding */}
                {viewMode === "list" && (
                  <div className="px-8 py-6">
                    <div className="space-y-4">
                      {filteredAndSortedNotices.map((notice) => (
                        <div
                          key={notice.id}
                          className={`bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow ${
                            notice.isPinned ? "border-blue-500 shadow-sm" : ""
                          } cursor-pointer hover:bg-hover transition-colors`}
                          onClick={() => navigate(`/notices/${notice.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                {getCategoryIcon(notice.category || "general")}
                                <span className="text-sm font-medium text-gray-600 capitalize">
                                  {notice.category || "General"}
                                </span>
                                {notice.isPinned && (
                                  <Pin className="w-4 h-4 text-blue-600" />
                                )}
                                {notice.metadata?.priority && (
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                                      notice.metadata.priority,
                                    )}`}
                                  >
                                    {notice.metadata.priority
                                      .charAt(0)
                                      .toUpperCase() +
                                      notice.metadata.priority.slice(1)}
                                  </span>
                                )}
                              </div>

                              <div>
                                <h3 className="font-semibold text-gray-900 text-xl mb-2">
                                  {notice.title}
                                </h3>

                                {notice.description && (
                                  <p className="text-gray-600 mb-3 line-clamp-2">
                                    {notice.description}
                                  </p>
                                )}

                                <div className="flex items-center gap-6 text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    <span>
                                      {notice.author?.email || "Anonymous"}
                                    </span>
                                  </div>
                                  <span>{formatDate(notice.createdAt)}</span>
                                  <div className="flex items-center gap-1">
                                    <Eye className="w-4 h-4" />
                                    <span>{notice.viewCount || 0}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Table View */}
                {viewMode === "table" && (
                  <div className="w-full">
                    {/* Table Body - No internal scroll */}
                    <div className="divide-y divide-gray-200">
                      {filteredAndSortedNotices.map((notice) => (
                        <div
                          key={notice.id}
                          className="px-8 py-6 hover:bg-hover transition-colors cursor-pointer"
                          onClick={() => navigate(`/notices/${notice.id}`)}
                        >
                          <div className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-3 min-w-0">
                              <div className="flex items-start">
                                {notice.isPinned && (
                                  <Pin className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {notice.title}
                                  </div>
                                  {notice.description && (
                                    <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                                      {notice.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-span-2 min-w-0 truncate">
                              <div className="flex items-center">
                                {getCategoryIcon(notice.category || "general")}
                                <span className="ml-2 text-sm text-gray-600 capitalize truncate">
                                  {notice.category || "General"}
                                </span>
                              </div>
                            </div>
                            <div className="col-span-1 min-w-0 truncate">
                              <span className="text-sm text-gray-600">
                                {notice.metadata?.priority
                                  ? notice.metadata.priority
                                      .charAt(0)
                                      .toUpperCase() +
                                    notice.metadata.priority.slice(1)
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="col-span-2 min-w-0 truncate">
                              <span className="text-sm text-gray-500 truncate block">
                                {notice.author?.email || "Anonymous"}
                              </span>
                            </div>
                            <div className={user?.isAdmin ? "col-span-2 min-w-0 truncate" : "col-span-3 min-w-0 truncate"}>
                              <span className="text-sm text-gray-500">
                                {formatDate(notice.createdAt)}
                              </span>
                            </div>
                            <div className="col-span-1 min-w-0 truncate">
                              <div className={cn("flex items-center gap-1", !user?.isAdmin && "justify-end")}>
                                <Eye className="w-4 h-4" />
                                <span>{notice.viewCount || 0}</span>
                              </div>
                            </div>
                            {user?.isAdmin && (
                              <div className="col-span-1 flex items-center justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                <EnhancedIconButton
                                  onClick={() => navigate(`/notices/${notice.id}/edit`)}
                                  icon={Edit3}
                                  title="Edit notice"
                                />
                                <EnhancedIconButton
                                  onClick={async () => {
                                    if (confirm("Are you sure you want to delete this notice?")) {
                                      try {
                                        // TODO: implement deleteNotice action if not already available
                                        // await deleteNoticeAction({ noticeId: notice.id });
                                        // refetchNotices();
                                        alert("Delete functionality to be implemented");
                                      } catch (err: any) {
                                        alert(err.message || "Failed to delete notice");
                                      }
                                    }
                                  }}
                                  icon={Trash2}
                                  title="Delete notice"
                                  variant="danger"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
